/**
 * Resolves which branch a customer-facing page belongs to.
 *
 * Resolution order:
 * 1. `?branch=<code>` in the URL (the branch's public QR/registration code) -
 *    lets any client's page work on any device without login.
 * 2. `branch_id` in localStorage (device where staff logged in once).
 *
 * There is deliberately NO fallback branch: an unidentified device gets an
 * error state instead of silently landing in another client's queue.
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getBranchInfoByCode, getBranchDisplayInfo } from "../api/api";
import { getCurrentUser } from "../utils/auth";
import { resolveQrDataUrl } from "../utils/qr";
import { getStorageUrl } from "../config/config";

const useBranchInfo = () => {
    const [searchParams] = useSearchParams();
    const branchCode = searchParams.get("branch");

    const [branchId, setBranchId] = useState(null);
    const [branchName, setBranchName] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [code, setCode] = useState(null);
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const resolveBranch = async () => {
            setLoading(true);
            setError(false);

            if (branchCode) {
                // URL code is authoritative: a bad code is an error, never
                // silently replaced by whatever is in localStorage.
                const data = await getBranchInfoByCode(branchCode);
                if (cancelled) return;
                if (data) {
                    const qr = await resolveQrDataUrl(
                        data.qr_data_url,
                        data.code
                    );
                    if (cancelled) return;
                    setBranchId(data.branch_id);
                    setBranchName(data.branch_name || null);
                    setQrDataUrl(qr || null);
                    setCode(data.code || null);
                    setSmsEnabled(data.sms_enabled === true);
                    setLogoUrl(
                        data.logo
                            ? getStorageUrl(`/storage/logos/${data.logo}`)
                            : null
                    );
                } else {
                    setError(true);
                }
                setLoading(false);
                return;
            }

            const storedId = parseInt(
                localStorage.getItem("branch_id"),
                10
            );
            if (!storedId) {
                setError(true);
                setLoading(false);
                return;
            }

            setBranchId(storedId);
            const data = await getBranchDisplayInfo(storedId);
            if (cancelled) return;
            if (data) {
                const qr = await resolveQrDataUrl(data.qr_data_url, data.code);
                if (cancelled) return;
                setBranchName(data.branch_name || null);
                setQrDataUrl(qr || null);
                setCode(data.code || null);
                setSmsEnabled(data.sms_enabled === true);
                setLogoUrl(
                    data.logo
                        ? getStorageUrl(`/storage/logos/${data.logo}`)
                        : null
                );
            } else {
                // Offline/API failure on a logged-in device: keep the branch
                // id and use the logged-in user's logo if available.
                const userLogo = getCurrentUser()?.logo;
                setLogoUrl(
                    userLogo
                        ? getStorageUrl(`/storage/logos/${userLogo}`)
                        : null
                );
            }
            setLoading(false);
        };

        resolveBranch();
        return () => {
            cancelled = true;
        };
    }, [branchCode]);

    // Appends the branch code to an internal path so navigation within the
    // customer flow keeps working on devices that were never logged in.
    const withBranch = (path) =>
        branchCode
            ? `${path}${path.includes("?") ? "&" : "?"}branch=${encodeURIComponent(
                  branchCode
              )}`
            : path;

    return {
        branchId,
        branchName,
        logoUrl,
        qrDataUrl,
        code,
        smsEnabled,
        loading,
        error,
        withBranch,
    };
};

export default useBranchInfo;
